import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

type UseFocusTrapOptions = {
  active: boolean
  containerRef: RefObject<HTMLElement | null>
  onEscape?: () => void
  /** Element to restore focus to when the trap deactivates (defaults to pre-open activeElement). */
  returnFocusRef?: RefObject<HTMLElement | null>
}

export function useFocusTrap({
  active,
  containerRef,
  onEscape,
  returnFocusRef,
}: UseFocusTrapOptions) {
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!active || !containerRef.current) {
      return
    }

    previousFocusRef.current = document.activeElement as HTMLElement | null

    const container = containerRef.current
    const returnFocusTarget = returnFocusRef?.current
    const focusable = Array.from(
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((element) => element.offsetParent !== null || element === document.activeElement)

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    queueMicrotask(() => first?.focus())

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape?.()
        return
      }

      if (event.key !== 'Tab' || focusable.length === 0) {
        return
      }

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault()
          last?.focus()
        }
        return
      }

      if (document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    container.addEventListener('keydown', onKeyDown)

    return () => {
      container.removeEventListener('keydown', onKeyDown)
      const restoreTarget = returnFocusTarget ?? previousFocusRef.current
      restoreTarget?.focus()
    }
  }, [active, containerRef, onEscape, returnFocusRef])
}
